import React, { useEffect, useState } from "react";
import "@/App.css";
import "./styles/oldMorfeuszReference.css";
import "./styles/morfeuszDarkOverrides.css";
import "./styles/mdCore.css";
import "./styles/pokoik.css";
import "./styles/pokoikLoadingRitual.css";
import "./styles/routeMotion.css";
import "./styles/addDream.css";
import "./styles/quickSave.css";
import "./styles/dreamView.css";
import "./styles/dreamAnalysisFlow.css";
import "./styles/mdDreamViews.css";
import "./styles/myDreams.css";
import "./styles/morfeuszPages.css";
import "./styles/homePageV2.css";
import "./styles/homePageVariant.css";
import "./styles/strefa.css";
import "./styles/strefaProfileFix.css";
import "./styles/store.css";
import "./styles/blog.css";
import "./styles/authFix.css";
import "./styles/debug.css";
import { LanguageProvider, useLang } from "./i18n/LanguageContext";
import { ThemeProvider, useTheme } from "./theme/ThemeContext";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { Header } from "./components/Header";
import { HomePageV2 } from "./components/HomePageV2";
import { HomePageVariant } from "./components/HomePageVariant";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";
import { PokoikPage } from "./features/dreams/pages/PokoikPage";
import { AddDreamPage } from "./features/dreams/pages/AddDreamPage";
import { QuickSavePage } from "./features/dreams/pages/QuickSavePage";
import { DreamViewPage } from "./features/dreams/pages/DreamViewPage";
import { EditDreamPage } from "./features/dreams/pages/EditDreamPage";
import { MyDreamsPage } from "./features/dreams/pages/MyDreamsPage";
import { LoginPage } from "./features/auth/pages/LoginPage";
import { RegisterPage } from "./features/auth/pages/RegisterPage";
import { ProfilePage } from "./features/auth/pages/ProfilePage";
import { StrefaPage } from "./features/settings/pages/StrefaPage";
import { LegalInfoPage } from "./features/settings/pages/LegalInfoPage";
import { SymbolFinderPage } from "./features/symbols/pages/SymbolFinderPage";
import { StorePage } from "./features/store/pages/StorePage";
import { StorePaymentCancelPage, StorePaymentSuccessPage } from "./features/store/pages/StorePaymentPage";
import { BlogPage } from "./features/blog/pages/BlogPage";

const PROTECTED_ROUTES = new Set([
  "pokoik",
  "moje-sny",
  "dodaj-sen",
  "edytuj-sen",
  "sen",
  "profil",
  "strefa",
]);

const SESSION_EXPIRED_NOTICE_KEY = "morfeusz:auth:sessionExpiredNotice";
const SESSION_EXPIRED_EVENT = "morfeusz:auth:sessionExpired";

const sessionExpiredCopy = {
  pl: {
    eyebrow: "Sesja",
    titlePre: "Księga",
    titleEm: "zamknięta",
    body: "Sesja wygasła albo konto otwarto na innym urządzeniu. Zaloguj się ponownie, aby wrócić do Morfeusza.",
    confirm: "Zaloguj ponownie",
  },
  en: {
    eyebrow: "Session",
    titlePre: "The book",
    titleEm: "is closed",
    body: "The session expired or the account was opened on another device. Sign in again to return to Morpheus.",
    confirm: "Sign in again",
  },
};

const isEditHash = (hash) => {
  if (!hash.startsWith("#/sen/")) return false;
  const queryStart = hash.indexOf("?");
  if (queryStart < 0) return false;
  const hashParams = new URLSearchParams(hash.slice(queryStart + 1));
  return hashParams.get("edit") === "1";
};

const getDebugEnabled = () => {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search || "").get("debug") === "1";
};

const getAppRoute = () => {
  if (typeof window === "undefined") return "home";
  const hash = window.location.hash || "";
  if (hash.startsWith("#/wariant-home") || hash.startsWith("#/home-v3")) return "wariant-home";
  if (hash.startsWith("#/blog")) return "blog";
  if (hash.startsWith("#/pokoik")) return "pokoik";
  if (hash.startsWith("#/moje-sny")) return "moje-sny";
  if (hash.startsWith("#/dodaj-sen")) return "dodaj-sen";
  if (hash.startsWith("#/szybki-zapis") || hash.startsWith("#/quick-save")) return "szybki-zapis";
  if (isEditHash(hash)) return "edytuj-sen";
  if (hash.startsWith("#/sen/")) return "sen";
  if (hash.startsWith("#/zaloguj") || hash.startsWith("#/login")) return "zaloguj";
  if (hash.startsWith("#/rejestracja") || hash.startsWith("#/register")) return "rejestracja";
  if (hash.startsWith("#/profil")) return "profil";
  if (hash.startsWith("#/strefa") || hash.startsWith("#/ustawienia")) return "strefa";
  if (hash.startsWith("#/platnosc/sukces")) return "platnosc-sukces";
  if (hash.startsWith("#/platnosc/anulowano")) return "platnosc-anulowano";
  if (hash.startsWith("#/sklep") || hash.startsWith("#/koszyk") || hash.startsWith("#/platnosc")) return "sklep";
  if (hash.startsWith("#/polityka-prywatnosci") || hash.startsWith("#/rodo") || hash.startsWith("#/regulamin") || hash.startsWith("#/kontakt")) return "legal-info";
  if (hash.startsWith("#/symbolika") || hash.startsWith("#/symbole") || hash.startsWith("#/szukaj-symbol") || hash.startsWith("#/symbol-finder")) return "symbolika";
  return "home";
};

const getCurrentHash = () => {
  if (typeof window === "undefined") return "";
  return window.location.hash || "";
};

const getHomeAnchorId = (hash) => {
  if (!hash || hash === "#" || hash === "#top") return "top";
  if (hash.startsWith("#/")) return "";
  return decodeURIComponent(hash.slice(1));
};

const scrollToHomeAnchor = (hash) => {
  if (typeof window === "undefined") return;
  const anchorId = getHomeAnchorId(hash);
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      if (!anchorId || anchorId === "top") { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); return; }
      const target = document.getElementById(anchorId);
      if (!target) { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); return; }
      target.scrollIntoView({ block: "start", inline: "nearest", behavior: "auto" });
    });
  });
};

const consumeExpiredSessionNotice = () => {
  if (typeof window === "undefined") return false;
  let found = false;
  try { found = Boolean(window.sessionStorage.getItem(SESSION_EXPIRED_NOTICE_KEY)); window.sessionStorage.removeItem(SESSION_EXPIRED_NOTICE_KEY); } catch (_) {}
  try { found = found || Boolean(window.localStorage.getItem(SESSION_EXPIRED_NOTICE_KEY)); window.localStorage.removeItem(SESSION_EXPIRED_NOTICE_KEY); } catch (_) {}
  return found;
};

const SessionExpiredModal = () => {
  const { lang } = useLang();
  const { theme } = useTheme();
  const copy = sessionExpiredCopy[lang] || sessionExpiredCopy.pl;
  const [visible, setVisible] = useState(consumeExpiredSessionNotice);
  useEffect(() => { const show = () => setVisible(true); window.addEventListener(SESSION_EXPIRED_EVENT, show); return () => window.removeEventListener(SESSION_EXPIRED_EVENT, show); }, []);
  if (!visible) return null;
  return <div className="mdDrawer__backdrop" role="dialog" aria-modal="true" data-ui="session-expired-modal" data-theme={theme}><div className="mdCard" data-ui="session-expired-card" onClick={(event) => event.stopPropagation()}><p className="mdPage__eyebrow">{copy.eyebrow}</p><h2 className="mdPage__title">{copy.titlePre} <em>{copy.titleEm}</em></h2><p className="mdPage__subtitle">{copy.body}</p><div className="mdRow" data-ui="session-expired-actions"><button type="button" className="mdBtn mdBtn--primary mdBtn--row" onClick={() => setVisible(false)} data-ui="session-expired-confirm">{copy.confirm}</button></div></div></div>;
};

const authRequiredCopy = {
  pl: {
    eyebrow: "Dostęp",
    titlePre: "Zaloguj się,",
    titleEm: "aby wejść dalej",
    body: "Ta część Morfeusza jest dostępna po zalogowaniu.\nZaloguj się albo utwórz konto, żeby wejść dalej.",
    signIn: "Zaloguj",
    createAccount: "Utwórz konto",
  },
  en: {
    eyebrow: "Access",
    titlePre: "Sign in",
    titleEm: "to continue",
    body: "This part of Morfeusz is available after signing in.\nSign in or create an account to continue.",
    signIn: "Sign in",
    createAccount: "Create account",
  },
};

const AuthRequiredGate = () => {
  const { lang } = useLang();
  const copy = authRequiredCopy[lang] || authRequiredCopy.pl;
  return (
    <section className="mdPage" data-testid="auth-required-gate">
      <div className="mdPage__inner">
        <div className="mdCard">
          <p className="mdPage__eyebrow">{copy.eyebrow}</p>
          <h1 className="mdPage__title">
            {copy.titlePre} <em>{copy.titleEm}</em>
          </h1>
          {copy.body.split("\n").map((line) => (
            <p className="mdPage__subtitle" key={line}>{line}</p>
          ))}
          <div className="mdRow">
            <a className="mdBtn mdBtn--primary mdBtn--row" href="#/zaloguj" data-testid="auth-required-login">{copy.signIn}</a>
            <a className="mdBtn mdBtn--row" href="#/rejestracja" data-testid="auth-required-register">{copy.createAccount}</a>
          </div>
        </div>
      </div>
    </section>
  );
};

const useAppRoute = () => {
  const [route, setRoute] = useState(getAppRoute);
  const [hash, setHash] = useState(getCurrentHash);
  useEffect(() => { const syncRoute = () => { setHash(getCurrentHash()); setRoute(getAppRoute()); }; window.addEventListener("hashchange", syncRoute); return () => window.removeEventListener("hashchange", syncRoute); }, []);
  useEffect(() => { scrollToHomeAnchor(hash); }, [route, hash]);
  return route;
};

const HomePage = () => <HomePageV2 />;

const AppRoute = ({ route }) => {
  const { lang } = useLang();
  if (route === "wariant-home") return <HomePageVariant />;
  if (route === "blog") return <BlogPage lang={lang} />;
  if (route === "pokoik") return <PokoikPage />;
  if (route === "moje-sny") return <MyDreamsPage />;
  if (route === "dodaj-sen") return <AddDreamPage />;
  if (route === "szybki-zapis") return <QuickSavePage />;
  if (route === "edytuj-sen") return <EditDreamPage />;
  if (route === "sen") return <DreamViewPage />;
  if (route === "zaloguj") return <LoginPage />;
  if (route === "rejestracja") return <RegisterPage />;
  if (route === "profil") return <ProfilePage />;
  if (route === "strefa") return <StrefaPage />;
  if (route === "platnosc-sukces") return <StorePaymentSuccessPage />;
  if (route === "platnosc-anulowano") return <StorePaymentCancelPage />;
  if (route === "sklep") return <StorePage />;
  if (route === "legal-info") return <LegalInfoPage />;
  if (route === "symbolika") return <SymbolFinderPage />;
  return <HomePage />;
};

const AppShell = () => {
  const { theme } = useTheme();
  const { authReady, isAuthenticated } = useAuth();
  const route = useAppRoute();
  const debugEnabled = getDebugEnabled();
  const shouldShowHomeForGuest = authReady && !isAuthenticated && PROTECTED_ROUTES.has(route);
  const effectiveRoute = shouldShowHomeForGuest ? "auth-required" : route;
  const shellClassName = ["min-h-screen font-body transition-colors duration-300", theme === "dark" ? "morfeusz-dark old-morfeusz-preview" : "bg-parchment text-ink"].join(" ");
  return <div className={shellClassName} data-theme-variant={theme} data-debug-enabled={debugEnabled ? "1" : "0"} data-testid="home-page"><Header /><main data-ui="app-main"><div data-ui="route-motion" key={effectiveRoute}>{shouldShowHomeForGuest ? <AuthRequiredGate /> : <AppRoute route={route} />}</div></main><Footer /><SessionExpiredModal /></div>;
};

function App() {
  return <ThemeProvider><LanguageProvider><AuthProvider><AppShell /><Toaster /></AuthProvider></LanguageProvider></ThemeProvider>;
}

export default App;